import React, { useEffect, useMemo, useState } from "react";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ListItemIcon from "@mui/material/ListItemIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, InputBase, ListItemButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import classnames from "classnames";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import { useConfigStore } from "../../store/config";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useCookieData } from "../../hook/useCookieData";
import { Close } from "@mui/icons-material";
import { useAuthStore } from "../../store/auth";

interface MenuItem {
  label: string;
  icon: string;
  path?: string;
  menuID: string;
  quantity?: string;
  visible?: boolean;
  children?: MenuItem[];
}

interface NestedMenuProps {
  // menuItems: MenuItem[];
}

const NestedMenu: React.FC<NestedMenuProps> = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [language] = useConfigStore((state) => [state.language]);
  const [isFreyrAdmin, isHistory, isPayloads, isTasks, isTemplates,] = useAuthStore((state) => [
    state.isFreyrAdmin, state.isHistory, state.isPayloads, state.isTasks, state.isTemplates,
  ]);
  const [keywords, setKeywords] = useState<string>("");

  const menuListMock = [
    {
      label: t("menu.templates"),
      icon: "",
      path: "/notification/templates",
      visible: isTemplates,
      menuID: "4-1",
      children: []
    },
    {
      label: t("menu.payloads"),
      icon: "",
      path: "/notification/payloads",
      visible: isPayloads,
      menuID: "4-2",
    },
    {
      label: t("menu.tasks"),
      icon: "",
      path: "/notification/tasks",
      visible: isTasks,
      menuID: "4-3",
    },
    {
      label: t("menu.history"),
      icon: "",
      path: "/notification/history",
      visible: isHistory,
      menuID: "4-4",
    },
  ];
  const [expandMenus, setExpandMenus] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [activeMenuID, setActiveMenuID] = useState<string>("");
  const location = useLocation();

  const handleMenuClick = (menuID: string, menu: MenuItem) => {
    if (menu.children && !menu.path) {
      setExpandMenus((prevState) => ({
        ...prevState,
        [menuID]: !prevState[menuID],
      }));
    } else {
      setActiveMenuID(menuID);
    }
    if (menu.path) {
      navigate(menu.path);
    }
  };

  // For refreshing： find par menu ids and activeMenu id
  const findActiveIdAndParentIdByPath = (
    tree: MenuItem[],
    targetPath: string,
    parentIDs: string[] = []
  ): { parentIDs: string[]; currentNodeID: string } => {
    for (const node of tree) {
      if (node.path && targetPath.includes(node.path)) {
        return { parentIDs, currentNodeID: node.menuID };
      }

      if (node?.children?.length) {
        const result = findActiveIdAndParentIdByPath(
          node.children,
          targetPath,
          [...parentIDs, node.menuID]
        );
        if (result.parentIDs.length > 0) {
          return result;
        }
      }
    }
    return { parentIDs: [], currentNodeID: "" };
  };

  // Refresh: activeMenu should highlight, par menus should expand
  useEffect(() => {
    const activeIdAndParentId = findActiveIdAndParentIdByPath(
      menuListMock,
      location.pathname
    );
    const parentIDs = activeIdAndParentId.parentIDs;
    setActiveMenuID(activeIdAndParentId.currentNodeID);
    for (let item of parentIDs) {
      setExpandMenus((prevState) => ({
        ...prevState,
        [item]: true,
      }));
    }
  }, [location.pathname]);

  // Search keyword filtering returns a menu structure with only keywords
  const filterMenu = (keywords: string, menuList: MenuItem[]): MenuItem[] => {
    return menuList
      .map((menu) => {
        if (menu.children && menu.visible) {
          return {
            ...menu,
            children: filterMenu(keywords, menu.children),
          };
        }
        return menu;
      })
      .filter(
        (menu) =>
          menu.label.toLowerCase().includes(keywords.toLowerCase()) ||
          (menu.children && menu.children.length > 0)
      );
  };

  const renderSubMenu = (parId: string, children: MenuItem[]) => {
    if (!children) return null;
    return (
      <Collapse in={expandMenus[parId]} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className="sub-menu">
          {children.map((subitem) => {
            if (subitem.visible) {
              return (
                <ListItemButton
                  key={subitem.menuID}
                  sx={{
                    pl: 4,
                  }}
                  // component={subitem.path ? Link : "div"}
                  // to={subitem.path}
                  className={classnames({
                    active: activeMenuID === subitem?.menuID,
                  })}
                  onClick={() => handleMenuClick(subitem.menuID, subitem)}
                >
                  {/* <ListItemIcon>{subitem.icon}</ListItemIcon> */}
                  <ListItemText primary={subitem.label} />
                </ListItemButton>
              );
            } else {
              return <></>;
            }
          })}
        </List>
      </Collapse>
    );
  };

  const renderMenu = (menuList: MenuItem[]) => {
    return (
      <List>
        {menuList.map((item) => {
          const activeFlag = activeMenuID === item.menuID;
          if (item.visible) {
            return (
              <Box key={item.menuID}>
                <ListItemButton
                  onClick={() => handleMenuClick(item.menuID, item)}
                  className={activeFlag ? "active" : ""}
                  // component={item.path ? Link : "div"}
                  // to={item.path}
                >
                  <ListItemIcon>
                    <i className={item.icon}></i>
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                  {(item.children?.length && (
                    <KeyboardArrowDownIcon
                      style={{
                        transform: expandMenus[item.menuID]
                          ? "rotate(-180deg)"
                          : "rotate(0)",
                      }}
                    />
                  )) ||
                    null}
                </ListItemButton>
                {item.children && renderSubMenu(item.menuID, item.children)}
              </Box>
            );
          } else {
            return <></>;
          }
        })}
      </List>
    );
  };

  const menuMap = useMemo(() => {
    const keywordsFilterResult = filterMenu(keywords, menuListMock);
    return keywordsFilterResult;
  }, [keywords, language]);

  return (
    <Box className={styles["side-menu"]}>
      <div className="search-input">
        <IconButton type="button">
          {!keywords && <FilterListIcon color="inherit"></FilterListIcon>}
        </IconButton>
        <InputBase
          placeholder={t("common.search")}
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
        />
        <IconButton type="button">
          {!!keywords && (
            <Close
              onClick={() => {
                setKeywords("");
              }}
            />
          )}
        </IconButton>
      </div>
      {renderMenu(menuMap)}
    </Box>
  );
};

export default NestedMenu;
